// Secure localStorage with encryption using the Web Crypto API
export class SecureStorage {
    constructor(secretKey) {
      // Convert the secret key to a suitable format for encryption
      this.keyPromise = this.generateKey(secretKey);
    }
  
    // Generate a cryptographic key from the provided secret
    async generateKey(secretKey) {
      // Convert the secret key string to an ArrayBuffer
      const encoder = new TextEncoder();
      const keyData = encoder.encode(secretKey);
      
      // Use SHA-256 to get a consistent key length
      const hashBuffer = await crypto.subtle.digest('SHA-256', keyData);
      
      // Import the key for AES-GCM algorithm
      return crypto.subtle.importKey(
        'raw',
        hashBuffer,
        { name: 'AES-GCM', length: 256 },
        false, // not extractable
        ['encrypt', 'decrypt'] // key usages
      );
    }
  
    // Encrypt data before storing
    async setItem(key, value) {
      try {
        const cryptoKey = await this.keyPromise;
        
        // Create a random initialization vector
        const iv = crypto.getRandomValues(new Uint8Array(12));
        
        // Encode the data
        const encoder = new TextEncoder();
        const data = encoder.encode(JSON.stringify(value));
        
        // Encrypt the data
        const encryptedData = await crypto.subtle.encrypt(
          { name: 'AES-GCM', iv },
          cryptoKey,
          data
        );
        
        // Store both the IV and encrypted data
        const encryptedObj = {
          iv: Array.from(iv),
          data: Array.from(new Uint8Array(encryptedData))
        };
        
        localStorage.setItem(key, JSON.stringify(encryptedObj));
        return true;
      } catch (error) {
        console.error('Encryption failed:', error);
        return false;
      }
    }
  
    // Decrypt data when retrieving
    async getItem(key) {
      try {
        const encryptedItem = localStorage.getItem(key);
        
        if (!encryptedItem) {
          return null;
        }
        
        const encryptedObj = JSON.parse(encryptedItem);
        const iv = new Uint8Array(encryptedObj.iv);
        const data = new Uint8Array(encryptedObj.data);
        const cryptoKey = await this.keyPromise;
        
        // Decrypt the data
        const decryptedData = await crypto.subtle.decrypt(
          { name: 'AES-GCM', iv },
          cryptoKey,
          data
        );
        
        // Decode the data
        const decoder = new TextDecoder();
        const decoded = decoder.decode(decryptedData);
        
        return JSON.parse(decoded);
      } catch (error) {
        console.error('Decryption failed:', error);
        return null;
      }
    }
  
    // Remove an item
    removeItem(key) {
      localStorage.removeItem(key);
    }
  
    // Clear all items
    clear() {
      localStorage.clear();
    }
  }
  
  // Usage example:
  // const secureStorage = new SecureStorage('your-secret-key');
  // 
  // // Store data securely
  // await secureStorage.setItem('userData', { username: 'john', role: 'admin' });
  // 
  // // Retrieve data
  // const userData = await secureStorage.getItem('userData');
  // console.log(userData); // { username: 'john', role: 'admin' }